import { Button, IconButton, SelectInput, SelectInputItem } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, FileUpload } from '@/shared/ui/atoms';
import type { FileWithProgress } from '@/shared/ui/atoms/FileUpload/types';
import { useClassStudents } from '../../../hooks/useClassStudents';
import { useEnrollmentDocuments } from '../../../hooks/useEnrollmentDocuments';

export interface UploadAttachmentSubmitData {
  readonly file: File;
  readonly classStudentId?: number;
  readonly enrollmentDocumentId?: number;
}

export interface UploadAttachmentModalProps {
  readonly personId: number;
  readonly open: boolean;
  readonly isLoading?: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (data: UploadAttachmentSubmitData) => void;
}

/**
 * Modal para upload de anexo de matrícula.
 *
 * Carrega as turmas e tipos de documento via API. O campo de arquivo é obrigatório;
 * turma e tipo de documento são opcionais.
 *
 * @param personId - ID da pessoa dona do anexo
 * @param open - Controla visibilidade do modal
 * @param isLoading - Exibe spinner no botão de envio enquanto a mutation está pendente
 * @param onClose - Callback ao fechar o modal
 * @param onSubmit - Callback com os dados do formulário validado
 */
export function UploadAttachmentModal({
  personId,
  open,
  isLoading = false,
  onClose,
  onSubmit,
}: UploadAttachmentModalProps) {
  const { t } = useTranslation('personManagement');

  const [selectedClassStudentId, setSelectedClassStudentId] = useState<number | undefined>(
    undefined,
  );
  const [selectedEnrollmentDocumentId, setSelectedEnrollmentDocumentId] = useState<
    number | undefined
  >(undefined);
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [fileError, setFileError] = useState(false);

  const {
    options: classStudents,
    isLoading: isLoadingClassStudents,
    isError: isErrorClassStudents,
  } = useClassStudents(personId);
  const {
    options: enrollmentDocuments,
    isLoading: isLoadingDocs,
    isError: isErrorDocs,
  } = useEnrollmentDocuments(personId, selectedClassStudentId);

  const hasNoEnrollments =
    !isLoadingClassStudents && !isErrorClassStudents && classStudents.length === 0;

  const selectedDocument = enrollmentDocuments.find(doc => doc.id === selectedEnrollmentDocumentId);
  const acceptedFormats = selectedDocument?.acceptedFormats ?? undefined;

  const resetForm = () => {
    setSelectedClassStudentId(undefined);
    setSelectedEnrollmentDocumentId(undefined);
    setFiles([]);
    setFileError(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleClassStudentChange = (value: string) => {
    const parsed = value === 'none' ? undefined : Number(value);
    setSelectedClassStudentId(parsed);
    setSelectedEnrollmentDocumentId(undefined);
  };

  const handleSubmit = () => {
    const firstFile = files[0];
    if (!firstFile) {
      setFileError(true);
      return;
    }
    onSubmit({
      file: firstFile.file,
      ...(selectedClassStudentId !== undefined ? { classStudentId: selectedClassStudentId } : {}),
      ...(selectedEnrollmentDocumentId !== undefined
        ? { enrollmentDocumentId: selectedEnrollmentDocumentId }
        : {}),
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-xs py-mini">
            <Dialog.Title className="ts-heading-sm text-ctx-content-title">
              {t('uploadModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('uploadModal.cancel')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-xs overflow-y-auto px-xs py-mini">
            {hasNoEnrollments ? (
              <Alert type="warning">{t('uploadModal.noEnrollmentsAlert')}</Alert>
            ) : (
              <>
                <SelectInput
                  label={t('uploadModal.course')}
                  value={
                    selectedClassStudentId !== undefined ? String(selectedClassStudentId) : 'none'
                  }
                  custom={{
                    content: 'z-[99999]',
                  }}
                  onValueChange={handleClassStudentChange}
                  disabled={isLoadingClassStudents}
                  hasError={isErrorClassStudents}
                  {...(isErrorClassStudents ? { helper: t('uploadModal.errorCourses') } : {})}
                >
                  <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                    {t('uploadModal.selectCourse')}
                  </SelectInputItem>
                  {classStudents.map(cs => (
                    <SelectInputItem
                      key={cs.id}
                      value={String(cs.id)}
                      custom={{ root: 'text-nowrap' }}
                    >
                      {cs.displayName}
                    </SelectInputItem>
                  ))}
                </SelectInput>

                <SelectInput
                  label={t('uploadModal.documentType')}
                  value={
                    selectedEnrollmentDocumentId !== undefined
                      ? String(selectedEnrollmentDocumentId)
                      : 'none'
                  }
                  custom={{
                    content: 'z-[99999]',
                  }}
                  onValueChange={v =>
                    setSelectedEnrollmentDocumentId(v === 'none' ? undefined : Number(v))
                  }
                  disabled={isLoadingDocs}
                  hasError={isErrorDocs}
                  {...(isErrorDocs
                    ? { helper: t('uploadModal.errorDocumentTypes') }
                    : !isLoadingDocs && enrollmentDocuments.length === 0
                      ? { helper: t('uploadModal.noDocumentTypes') }
                      : {})}
                >
                  <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                    {t('uploadModal.selectDocumentType')}
                  </SelectInputItem>
                  {enrollmentDocuments.map(doc => (
                    <SelectInputItem
                      key={doc.id}
                      value={String(doc.id)}
                      custom={{ root: 'text-nowrap' }}
                    >
                      {doc.name}
                      {doc.isRequired ? ' *' : ''}
                    </SelectInputItem>
                  ))}
                </SelectInput>

                <div className="flex flex-col gap-quark">
                  <span className="ts-caption-xxs text-ctx-content-base">
                    {t('uploadModal.file')}
                  </span>
                  <FileUpload
                    files={files}
                    addFiles={newFiles => {
                      setFiles(newFiles.map(f => ({ file: f })));
                      setFileError(false);
                    }}
                    removeFile={name => setFiles(prev => prev.filter(f => f.file.name !== name))}
                    hasError={fileError}
                    {...(fileError ? { helper: t('uploadModal.fileRequired') } : {})}
                    {...(acceptedFormats !== undefined ? { accept: acceptedFormats } : {})}
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-xs py-mini">
            <Button hierarchy="secondary" size="lg" onClick={handleClose} disabled={isLoading}>
              {t('uploadModal.cancel')}
            </Button>
            <Button
              hierarchy="primary"
              size="lg"
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={hasNoEnrollments}
            >
              {t('uploadModal.submit')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
